import React, { useState, useEffect, useMemo } from "react";
import Wins from "../components/Wins";
import DailyJournal from "../components/DailyJournal";
import AskJane from "../components/AskJane";
import Videos from "../components/Videos";
import Products from "../components/Products";
import Articles from "../components/Articles";
import { quoteMessage, appVersion } from "../services/constants";
import {
  getProgress,
  getResources,
  getRomGallery,
  getLastCheckIn,
} from "../Api";
import secureLocalStorage from "react-secure-storage";
import { Link } from "react-router-dom";
import Modal from "react-modal";
import confetti from "canvas-confetti";

import "../css/Modal.css";

const Home = () => {
  const [patientInfo, setPatientInfo] = useState(null);
  const [romGallery, setRomGallery] = useState([]);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [progress, setProgress] = useState([]);
  const [weekNo, setWeekNo] = useState(0);
  const [resources, setResources] = useState([]);
  const [articles, setArticles] = useState([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [surgeryStatus, setSurgeryStatus] = useState([null]);
  const [lastCheckDate, setLastCheckDate] = useState(0);
  const [isPreventive, setIsPreventive] = useState(false);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const getRandomMessage = () =>
    quoteMessage[Math.floor(Math.random() * quoteMessage.length)];

  const [oPopupMessage, setPopupMessage] = useState(getRandomMessage);

  const getPatientId = () => {
    return secureLocalStorage.getItem("patient_id");
  };

  const fetchResources = async (week_no, isPreventive) => {
    try {
      const response = await getResources(week_no, "VIDEO", isPreventive);
      setResources(response);
    } catch (error) {
      console.error("Error fetching VIDEO:", error);
    }
  };

  const fetchArticles = async (week_no, isPreventive) => {
    try {
      const response = await getResources(week_no, "ARTICLE", isPreventive);
      setArticles(response);
    } catch (error) {
      console.error("Error fetching ARTICLE:", error);
    }
  };

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const response = await getProgress(getPatientId());
        secureLocalStorage.setItem("week_no", response.patient_info.week_no);
        secureLocalStorage.setItem("patient_info", response.patient_info);
        setProgress(response);
        setWeekNo(response.patient_info.week_no);

        if (response.survey_due) {
          window.location.href = "/check-in";
        }

        if (response.patient_info.last_checkin_date) {
          const lastCheckDate = new Date(
            response.patient_info.last_checkin_date
          );
          setLastCheckDate(lastCheckDate.toDateString());
        }
        setPatientInfo(response.patient_info);
        setIsPreventive(response.patient_info.is_preventive);
        fetchResources(
          response.patient_info.week_no,
          response.patient_info.is_preventive
        );
        fetchArticles(
          response.patient_info.week_no,
          response.patient_info.is_preventive
        );
        setTasks(response.weekly_task_completion || []);

        // ✅ Force re-render after data is fetched
        setIsDataFetched(true);
      } catch (error) {
        console.error("Error fetching progress:", error);
      }
    };

    fetchProgress();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("onboarding") === "true") {
      setIsOpen(true);
      confetti();
    }
  }, []);

  const today = useMemo(() => new Date(), []);

  const currentDate = useMemo(() => new Date(), []);

  const generateWeekAndStatus = useMemo(() => {
    const surgeryDate = new Date(patientInfo?.start_date);

    const weeksDiff = Math.abs(patientInfo?.week_no);

    let message = "";
    let typeStr = "";
    if (patientInfo?.is_preventive == 1) {
      message = `Week ${weeksDiff} of Prehab`;
      typeStr = "Prevention";
    }

    if (patientInfo?.is_preventive == 0) {
      if (surgeryDate > currentDate) {
        typeStr = "Pre-Op";
        message = `${weeksDiff} ${
          weeksDiff === 1 ? "Week" : "Weeks"
        } Until Your Big Day`;
      } else {
        typeStr = "Post-Op";
        message = `Week ${weeksDiff} of Your Recovery`;
      }
    }

    setSurgeryStatus(typeStr);

    return { message, typeStr };
  }, [patientInfo, currentDate]);

  return (
    <main className="main-content">
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => {}}
        shouldCloseOnOverlayClick={false}
        contentLabel="Onboarding Modal"
        className="onboarding-modal fade1"
      >
        <h2>Day 1</h2>
        <div className="quoted-text">
          <p>{oPopupMessage}</p>
          <p className="my-knee-journey">MyKneeJourney</p>
        </div>
        <div className="centered-texts">
          <a className="engivio-text" href="https://engivio.com/">
            www.engivio.com
          </a>
          {/* <a className="return" onClick={() => setIsOpen(false)}></a> */}
          <a className="return" href="/">
            Return to Home
          </a>
        </div>
        {/* <button onClick={() => setIsOpen(false)}>Close</button> */}
      </Modal>
      <div className="main fade-med">
        <div className="headContents mb-20">
          <div className="headContent-left">
            <>
              <h2 className="heading">{generateWeekAndStatus.message}</h2>
              <p className="subHeading">
                <span>{generateWeekAndStatus.typeStr}</span> -{" "}
                {currentDate.toDateString()}
              </p>
            </>
          </div>
          <div className="headContent-right">
            <div className="streakContainer">
              <h4>Streak</h4>
              {/* <p>{patientInfo?.streak}</p> */}
              <p>1</p>
            </div>
            <div className="romContainer">
              <img
                className="headerContentSeparator"
                src="../headerContentSeparator.svg"
              />
              <h4>ROM Personal Best</h4>
              <p className="romBest">
                {romGallery.length > 0
                  ? Math.max(...romGallery.map((item) => item.rom_value))
                  : 0}
                °
              </p>
            </div>
          </div>
        </div>
        <div className="journal-ask">
          {isDataFetched && patientInfo && (
            <DailyJournal
              patientId={getPatientId()}
              lastCheckDate={lastCheckDate}
              isPreventive={isPreventive}
            />
          )}

          <AskJane surgeryStatus={surgeryStatus} weekNo={weekNo} />
        </div>
        <div className="wins-insights">
          <Wins
            tasks={tasks}
            completedCount={completedCount}
            isPreventive={isPreventive}
          />
        </div>
        <Videos resources={resources} />
        <Articles resources={articles} />
        {/* <Products /> */}
        <Link to="/logout" className="logout-button app-version">
          <b>({appVersion})</b> Logout
        </Link>
      </div>
    </main>
  );
};

export default Home;
